import {
  ColumnPinningState,
  SortingState,
  createColumnHelper,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useCallback, useMemo, useRef, useState, ReactNode } from 'react';
import styles from './slot-summary-table.module.scss';
import classNames from 'classnames';
import { DataTableSkeleton, Button } from '@carbon/react';
import { Download } from '@carbon/react/icons';
import NoDataSvg from 'images/srp-illustration-data.svg';
import { Toggle } from 'components/toggle/toggle';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import { useCsvExport } from 'lib/hooks/useCsvExport';

import { DndContext, DragMoveEvent, closestCenter } from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  SortableContext,
  horizontalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useColumnState } from 'lib/hooks/useColumnState';
import { capitalize } from 'lodash-es';
import usePinnedColumnReordering from 'lib/hooks/usePinnedColumnReordering';
import { MIN_WIDTH } from 'components/table/column-actions';

import {
  DragCell,
  DragHeader,
  getInitialColumnOrder,
  handleDragEnd,
  handleDragMove,
} from 'components/table/draggable';
import { formatNumber, formatCountWithPercent } from 'utils/number';
import { SlotVerificationCounts, NriVerificationCounts } from './types';
import {
  WELL_STATUS_COUNT_COLUMNS,
  type WellStatusCounts,
} from 'utils/slot-categories';

/** Each Create New Well status is its own column (WellStatusCounts, from the shared list). */
export interface SlotSummary extends WellStatusCounts {
  wellType: string;
  producing: number;
  duc: number;
  permit: number;
  ltd: number;
  shutIn: number;
  inactive: number;
  expiredPermit: number;
  wellboreExcluded?: number;
  total: number;

  // Nested verification metrics
  slot?: SlotVerificationCounts;
  nri?: NriVerificationCounts;
}

export type SlotSummaryDisplayMode = 'none' | 'slots' | 'nris';

const columnHelper = createColumnHelper<SlotSummary>();

const WellTypeCell = ({
  value,
  showHighlight,
}: {
  value: ReactNode;
  showHighlight: boolean;
}) => {
  return (
    <div
      className={classNames(
        styles.cell,
        styles.boldNumber,
        showHighlight && styles.wellStatus
      )}
    >
      {value}
    </div>
  );
};

// Get base columns without expired permit (added conditionally based on toggle)
const getBaseColumns = (
  showHighlight: boolean,
  excludeLtds: boolean,
  showExpiredPermits: boolean
) => [
  columnHelper.accessor('wellType', {
    id: 'wellType',
    header: 'Well Type',
    cell: (info) => (
      <div className={styles.cell}>{capitalize(info.getValue())}</div>
    ),
  }),
  columnHelper.accessor('producing', {
    id: 'producing',
    header: 'Producing',
    cell: (info) => (
      <WellTypeCell
        value={formatNumber(info.getValue(), 0, 0, 'decimal', true)}
        showHighlight={showHighlight}
      />
    ),
  }),
  columnHelper.accessor('duc', {
    id: 'duc',
    header: 'DUC',
    cell: (info) => (
      <WellTypeCell
        value={formatNumber(info.getValue(), 0, 0, 'decimal', true)}
        showHighlight={showHighlight}
      />
    ),
  }),
  columnHelper.accessor('permit', {
    id: 'permit',
    header: 'Permit',
    cell: (info) => (
      <WellTypeCell
        value={formatNumber(info.getValue(), 0, 0, 'decimal', true)}
        showHighlight={showHighlight}
      />
    ),
  }),
  columnHelper.accessor('ltd', {
    id: 'ltd',
    header: 'LTD',
    cell: (info) => (
      <div
        className={classNames(styles.cell, {
          [styles.excludedColumn]: excludeLtds,
        })}
      >
        {formatNumber(info.getValue(), 0, 0, 'decimal', true)}
      </div>
    ),
  }),
  columnHelper.accessor('shutIn', {
    id: 'shutIn',
    header: 'Shut In',
    cell: (info) => (
      <WellTypeCell
        value={formatNumber(info.getValue(), 0, 0, 'decimal', true)}
        showHighlight={showHighlight}
      />
    ),
  }),
  columnHelper.accessor('inactive', {
    id: 'inactive',
    header: 'Inactive',
    cell: (info) => (
      <WellTypeCell
        value={formatNumber(info.getValue(), 0, 0, 'decimal', true)}
        showHighlight={showHighlight}
      />
    ),
  }),
  ...WELL_STATUS_COUNT_COLUMNS.map(({ key, label }) =>
    columnHelper.accessor(key, {
      id: key,
      header: label,
      cell: (info) => (
        <WellTypeCell
          value={formatNumber(info.getValue() ?? 0, 0, 0, 'decimal', true)}
          showHighlight={showHighlight}
        />
      ),
    })
  ),
  columnHelper.accessor('wellboreExcluded', {
    id: 'wellboreExcluded',
    header: 'Wellbore Excluded',
    cell: (info) => (
      <div className={classNames(styles.cell, styles.excludedColumn)}>
        {formatNumber(info.getValue() ?? 0, 0, 0, 'decimal', true)}
      </div>
    ),
  }),
  // Total column (API total already excludes expired permit and wellbore-excluded wells)
  columnHelper.display({
    id: 'total',
    header: 'Total',
    cell: (info) => {
      const row = info.row.original;
      // API total already excludes expired permit; only subtract LTD when toggle is on
      const total = excludeLtds ? row.total - row.ltd : row.total;
      return (
        <div className={classNames(styles.cell, styles.boldNumber)}>
          {formatNumber(total, 0, 0, 'decimal', true)}
        </div>
      );
    },
  }),
  // Add Expired Permit column after Total when showExpiredPermits is ON
  ...(showExpiredPermits
    ? [
        columnHelper.accessor('expiredPermit', {
          id: 'expiredPermit',
          header: 'Expired Permit',
          cell: (info) => (
            <div className={styles.cell}>
              {formatNumber(info.getValue(), 0, 0, 'decimal', true)}
            </div>
          ),
        }),
      ]
    : []),
];

// Slot-level verification columns (for Slots tab)
const getSlotsVerificationColumns = () => [
  columnHelper.display({
    id: 'slotsVerified',
    header: 'Slots Verified',
    cell: (info) => {
      const slot = info.row.original.slot;
      return (
        <div className={classNames(styles.cell, styles.boldNumber)}>
          {formatCountWithPercent(slot?.verified ?? 0, slot?.total ?? 0)}
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'slotsInProgress',
    header: 'Slots In Progress',
    cell: (info) => {
      const slot = info.row.original.slot;
      return (
        <div className={classNames(styles.cell, styles.boldNumber)}>
          {formatCountWithPercent(slot?.inProgress ?? 0, slot?.total ?? 0)}
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'slotsNotStarted',
    header: 'Slots Not Started',
    cell: (info) => {
      const slot = info.row.original.slot;
      return (
        <div className={classNames(styles.cell, styles.boldNumber)}>
          {formatCountWithPercent(slot?.notStarted ?? 0, slot?.total ?? 0)}
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'slotsIncorrect',
    header: 'Slots Incorrect',
    cell: (info) => {
      const slot = info.row.original.slot;
      return (
        <div className={classNames(styles.cell, styles.boldNumber)}>
          {formatCountWithPercent(slot?.incorrect ?? 0, slot?.total ?? 0)}
        </div>
      );
    },
  }),
];

// NRI-level verification columns (for Slot Allocations tab)
const getNrisVerificationColumns = () => [
  columnHelper.display({
    id: 'totalNris',
    header: 'Total NRIs',
    cell: (info) => {
      const nri = info.row.original.nri;
      return (
        <div className={classNames(styles.cell, styles.boldNumber)}>
          {formatNumber(nri?.total ?? 0, 0, 0, 'decimal', true)}
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'nrisVerified',
    header: 'NRIs Verified',
    cell: (info) => {
      const nri = info.row.original.nri;
      return (
        <div className={classNames(styles.cell, styles.boldNumber)}>
          {formatCountWithPercent(nri?.verified ?? 0, nri?.total ?? 0)}
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'nrisNotStarted',
    header: 'NRIs Not Started',
    cell: (info) => {
      const nri = info.row.original.nri;
      return (
        <div className={classNames(styles.cell, styles.boldNumber)}>
          {formatCountWithPercent(nri?.notStarted ?? 0, nri?.total ?? 0)}
        </div>
      );
    },
  }),
  columnHelper.display({
    id: 'nrisIncorrect',
    header: 'NRIs Incorrect',
    cell: (info) => {
      const nri = info.row.original.nri;
      return (
        <div className={classNames(styles.cell, styles.boldNumber)}>
          {formatCountWithPercent(nri?.incorrect ?? 0, nri?.total ?? 0)}
        </div>
      );
    },
  }),
];

export const SlotSummaryTable = ({
  slotSummary,
  isLoading,
  displayMode = 'none',
  showHighlight = true,
  tableId,
  excludeLtds = false,
  onExcludeLtdsToggle = () => {},
  showExpiredPermits = false,
  onShowExpiredPermitsToggle = () => {},
}: {
  slotSummary: SlotSummary[];
  isLoading: boolean;
  displayMode?: SlotSummaryDisplayMode;
  showHighlight?: boolean;
  tableId: string;
  /** When ON: Subtracts LTD from Total, greys out LTD column */
  excludeLtds?: boolean;
  onExcludeLtdsToggle?: (checked: boolean) => void;
  /** When ON: Shows Expired Permit column after Total column */
  showExpiredPermits?: boolean;
  onShowExpiredPermitsToggle?: (checked: boolean) => void;
}) => {
  const data = useMemo(() => slotSummary, [slotSummary]);
  const { exportToCsv, isExporting } = useCsvExport();

  const columns = useMemo(() => {
    const baseColumns = getBaseColumns(
      showHighlight,
      excludeLtds,
      showExpiredPermits
    );

    if (displayMode === 'slots') {
      return [...baseColumns, ...getSlotsVerificationColumns()];
    }
    if (displayMode === 'nris') {
      return [...baseColumns, ...getNrisVerificationColumns()];
    }

    return baseColumns;
  }, [displayMode, showHighlight, excludeLtds, showExpiredPermits]);

  const handleExport = async () => {
    // Base columns without Expired Permit (matches display when toggle is OFF)
    const baseColumns = [
      { key: 'wellType', header: 'Well Type' },
      { key: 'producing', header: 'Producing' },
      { key: 'duc', header: 'DUC' },
      { key: 'permit', header: 'Permit' },
      { key: 'ltd', header: excludeLtds ? 'LTD (Excluded)' : 'LTD' },
      { key: 'shutIn', header: 'Shut In' },
      { key: 'inactive', header: 'Inactive' },
      ...WELL_STATUS_COUNT_COLUMNS.map(({ key, label }) => ({
        key,
        header: label,
      })),
      { key: 'wellboreExcluded', header: 'Wellbore Excluded (Excluded)' },
      { key: 'total', header: 'Total' },
    ];

    // Add Expired Permit column after Total when showExpiredPermits is ON (WYSIWYG)
    if (showExpiredPermits) {
      baseColumns.push({
        key: 'expiredPermit',
        header: 'Expired Permit (Excluded)',
      });
    }

    const slotsVerificationColumns = [
      { key: 'slotsVerified', header: 'Slots Verified' },
      { key: 'slotsInProgress', header: 'Slots In Progress' },
      { key: 'slotsNotStarted', header: 'Slots Not Started' },
      { key: 'slotsIncorrect', header: 'Slots Incorrect' },
    ];

    const nrisVerificationColumns = [
      { key: 'totalNris', header: 'Total NRIs' },
      { key: 'nrisVerified', header: 'NRIs Verified' },
      { key: 'nrisNotStarted', header: 'NRIs Not Started' },
      { key: 'nrisIncorrect', header: 'NRIs Incorrect' },
    ];

    const exportColumns =
      displayMode === 'slots'
        ? [...baseColumns, ...slotsVerificationColumns]
        : displayMode === 'nris'
          ? [...baseColumns, ...nrisVerificationColumns]
          : baseColumns;

    const exportData = slotSummary.map((row) => ({
      wellType: row.wellType,
      producing: row.producing,
      duc: row.duc,
      permit: row.permit,
      ltd: row.ltd,
      shutIn: row.shutIn,
      inactive: row.inactive,
      ...Object.fromEntries(
        WELL_STATUS_COUNT_COLUMNS.map(({ key }) => [key, row[key] ?? 0])
      ),
      wellboreExcluded: row.wellboreExcluded ?? 0,
      // Total already excludes expired permit and wellbore-excluded at API level; only subtract LTD when toggle is ON
      total: excludeLtds ? row.total - row.ltd : row.total,
      // Include expired permit only when toggle is ON (WYSIWYG)
      expiredPermit: row.expiredPermit,
      // Slots verification columns
      slotsVerified: row.slot?.verified ?? 0,
      slotsInProgress: row.slot?.inProgress ?? 0,
      slotsNotStarted: row.slot?.notStarted ?? 0,
      slotsIncorrect: row.slot?.incorrect ?? 0,
      // NRIs verification columns
      totalNris: row.nri?.total ?? 0,
      nrisVerified: row.nri?.verified ?? 0,
      nrisNotStarted: row.nri?.notStarted ?? 0,
      nrisIncorrect: row.nri?.incorrect ?? 0,
    }));

    await exportToCsv({
      filename: 'slot-summary',
      columns: exportColumns,
      data: exportData,
    });
  };

  const [sorting, setSorting] = useState<SortingState>([]);

  const initialColumnPinning: ColumnPinningState = {
    left: ['wellType'],
  };

  const initialColumnOrder = useMemo(
    () => getInitialColumnOrder(columns),
    [columns]
  );

  const { columnOrder, setColumnOrder, columnPinning, setColumnPinning } =
    useColumnState({
      tableId,
      initialColumnOrder,
      initialColumnPinning,
    });

  const hoveredColumnIdRef = useRef<string | null>(null);

  usePinnedColumnReordering(columnPinning, columnOrder, setColumnOrder);

  const onDragMove = useCallback(
    (e: DragMoveEvent) => {
      handleDragMove(e, hoveredColumnIdRef);
    },
    [hoveredColumnIdRef]
  );

  const table = useReactTable({
    data,
    columns,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: MIN_WIDTH,
    },
    state: {
      sorting,
      columnOrder,
      columnPinning,
    },
    onSortingChange: setSorting,
    onColumnOrderChange: setColumnOrder,
    onColumnPinningChange: setColumnPinning,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    initialState: {
      columnPinning: initialColumnPinning,
      columnOrder: initialColumnOrder,
    },
  });

  if (isLoading) {
    return (
      <DataTableSkeleton
        data-testid="slot-summary-table-skeleton"
        columnCount={columns.length}
        rowCount={2}
        showHeader={false}
        showToolbar={false}
      />
    );
  }

  return (
    <div className={styles.wrapper}>
      <div className={styles.toolbar}>
        {displayMode !== 'none' && (
          <div className={styles.toggleSection}>
            <Toggle isActive={excludeLtds} handleToggle={onExcludeLtdsToggle} />
            <span className={styles.toggleLabel}>Exclude LTDs</span>
          </div>
        )}
        {displayMode !== 'none' && (
          <div className={styles.toggleSection}>
            <Toggle
              isActive={showExpiredPermits}
              handleToggle={onShowExpiredPermitsToggle}
            />
            <span className={styles.toggleLabel}>Show Expired Permits</span>
          </div>
        )}
        <FloatingTooltip content="Export">
          <Button
            hasIconOnly
            renderIcon={Download}
            iconDescription="Export"
            kind="ghost"
            size="lg"
            disabled={isExporting || slotSummary.length === 0}
            onClick={handleExport}
          />
        </FloatingTooltip>
      </div>
      <DndContext
        collisionDetection={closestCenter}
        onDragEnd={(e) =>
          handleDragEnd(
            e,
            columnPinning,
            columnOrder,
            setColumnOrder,
            setColumnPinning,
            hoveredColumnIdRef
          )
        }
        modifiers={[restrictToHorizontalAxis]}
        onDragMove={onDragMove}
      >
        <div className={styles.tableContainer}>
          <table className={styles.table}>
            <thead>
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id}>
                  <SortableContext
                    items={columnOrder}
                    strategy={horizontalListSortingStrategy}
                  >
                    {headerGroup.headers.map((header) => (
                      <DragHeader
                        key={header.id}
                        header={header}
                        hoveredColumnIdRef={hoveredColumnIdRef}
                      />
                    ))}
                  </SortableContext>
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className={styles.emptyCell}>
                    <div className={styles.emptyState}>
                      <img src={NoDataSvg} alt="No data" />
                      <p className={styles.emptyStateText}>
                        No slot summary data available
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr key={row.id}>
                    <SortableContext
                      items={columnOrder}
                      strategy={horizontalListSortingStrategy}
                    >
                      {row.getVisibleCells().map((cell) => (
                        <DragCell
                          key={cell.id}
                          cell={cell}
                          hoveredColumnIdRef={hoveredColumnIdRef}
                        />
                      ))}
                    </SortableContext>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </DndContext>
    </div>
  );
};
